// hooks/useClientesYEquipos.js
import { useCallback, useEffect, useState } from "react";
import axios from "../../../config/axiosConfig";
import { extractList, buildEquiposMap } from "../utils/extract";

export const useClientesYEquipos = ({ excluirServicio = "" } = {}) => {
  const [clientes, setClientes] = useState([]);
  const [equiposMap, setEquiposMap] = useState({});
  const [servicios, setServicios] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(false);
  const sucursal = "";
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalClientes, setTotalClientes] = useState(0);
  const [lastFilters, setLastFilters] = useState(null);

  const normalizeEstado = (v) => (!v || v === "Todos" ? undefined : v);

  const normalizeCliente = (cli) => ({
    ...cli,
    nombre_cliente: cli.razon_social ?? cli.nombre_cliente ?? cli.nombre ?? "",
    nombre_servicio: cli.nombre_servicio ?? cli.servicio ?? "",
    sucursal_facturar: cli.sucursal_facturar ?? cli.departamento ?? "",
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoadingServicios(true);
      try {
        const catalogosResp = await axios.get("/api/clientes/catalogos");

        if (!mounted) return;

        const serviciosCatalogo =
          catalogosResp?.data?.servicios ?? catalogosResp?.data?.data?.servicios ?? [];
        setServicios(Array.isArray(serviciosCatalogo) ? serviciosCatalogo : []);
      } finally {
        if (mounted) setLoadingServicios(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const getResponseTotal = (data, fallback) =>
    Number(data?.total ?? data?.data?.total ?? fallback ?? 0);

  const search = async ({
    nombreCliente,
    estado,
    codServicio,
    sucursal: sucursalFiltro,
    page: pageArg = 0,
    pageSize: pageSizeArg = rowsPerPage,
  }) => {
    const q = (nombreCliente || "").trim();
    if (!q) return;
    setSearchPerformed(true);
    setSearching(true);
    setPage(pageArg);
    setRowsPerPage(pageSizeArg);

    const filters = {
      nombreCliente: q,
      estado,
      codServicio,
      sucursal: sucursalFiltro || sucursal || "",
    };
    setLastFilters(filters);

    const params = {
      buscar: q,
      page: pageArg + 1,
      pageSize: pageSizeArg,
      solo_nombre: 1, // busca por cliente, grupo y nombre (no por descripción)
    };
    const est = normalizeEstado(estado);
    if (est !== undefined) params.estado = est;
    if (codServicio) params.cod_servicio = codServicio;
    if (filters.sucursal) params.sucursal = filters.sucursal;
    if (excluirServicio) params.excluir_servicio = excluirServicio;

    try {
      const [respCli, respEq] = await Promise.all([
        axios.get("/api/req-solicitudes/catalogo-clientes-servicio", { params }),
        axios.get("/api/cliente-servicio/equipos"),
      ]);
      const listClientes = extractList(respCli?.data);
      const listEquipos = extractList(respEq?.data);
      setClientes(Array.isArray(listClientes) ? listClientes.map(normalizeCliente) : []);
      setTotalClientes(getResponseTotal(respCli?.data, listClientes.length));
      setEquiposMap(buildEquiposMap(listEquipos));
    } finally {
      setSearching(false);
    }
  };

  const changePage = (nextPage) => {
    if (!lastFilters) return;
    search({ ...lastFilters, page: nextPage, pageSize: rowsPerPage });
  };

  const changeRowsPerPage = (nextRowsPerPage) => {
    if (!lastFilters) return;
    search({ ...lastFilters, page: 0, pageSize: nextRowsPerPage });
  };

  const updateClienteLocal = useCallback((codServicioCliente, patch) => {
    setClientes((prev) =>
      prev.map((cli) =>
        String(cli.cod_servicio_cliente) === String(codServicioCliente)
          ? { ...cli, ...patch }
          : cli
      )
    );
  }, []);

  return {
    clientes,
    equiposMap,
    servicios,
    sucursal,
    loadingServicios,
    searching,
    searchPerformed,
    page,
    rowsPerPage,
    totalClientes,
    search,
    changePage,
    changeRowsPerPage,
    updateClienteLocal,
  };
};
